<script lang="ts">import { goto } from "$app/navigation";
import { base } from "$app/paths";
import { page } from "$app/stores";
import { Button } from "@infinitro/button";
import { cfgBrandTitle, cfgCdnBase, cfgToThemeContentPath, FeatureSet, ReleaseStage, stateFeatureSet, stateTheme, stateUi, wantAtLeastReleaseStage } from "@infinitro/config";
import { Container } from "@infinitro/container";
import { t, Verbiage } from "@infinitro/content";
import { isMobile, locale, loggedIn, ModalDialog, modalStack, pushModal, refreshRaces, stateRaces, toSiteUrl } from "@infinitro/global";
import { InfoCard } from "@infinitro/info-card";
import { NotFound } from "@infinitro/not-found";
import { Race } from "@infinitro/races";
import MarkdownIt from "markdown-it";
import { onDestroy, onMount } from "svelte";
import { derived, get } from "svelte/store";
import { getPromotions } from "./data";
import PromoItemCustom from "./parts/promo-item-custom.svelte";
import PromoItemMomentum from "./parts/promo-item-momentum.svelte";
import PromoItem from "./parts/promo-item.svelte";
import ActionButton from "./parts/promo-item/action-button.svelte";
import { mapRacesToPromotions } from "./state";
export let isFlushRace = false;
export let promo;
const CDN_BASE = cfgCdnBase();
const md = new MarkdownIt({
	html: true,
	breaks: true,
	linkify: true
});
const _normalizeLink = md.normalizeLink.bind(md);
md.normalizeLink = (url) => {
	const n = _normalizeLink(url).replace(/^http:\/\//, "https://");
	return n.startsWith("/") && !n.startsWith("//") && base ? base + n : n;
};
// Wrap tables so they can scroll horizontally inside the InfoCard overflow:hidden boundary
md.renderer.rules.table_open = (tokens, idx, options, _, self) => "<div class=\"table-scroll-wrapper\">" + self.renderToken(tokens, idx, options);
md.renderer.rules.table_close = (tokens, idx, options, _, self) => self.renderToken(tokens, idx, options) + "</div>";
const CTA_TOKEN_REGEX = /\[\[(.*?)\|(.*?)\]\]|\{\{(.*?)\|(.*?)\}\}|\(\((.*?)\|(.*?)\)\)/g;
/** HTML comment in promo markdown: content after this marker renders in a new InfoCard below. CTA tokens only apply to the first section. */
const PROMO_NEXT_CARD_MARKER = /<!--\s*promo-next-card\s*-->/gi;
let content;
let htmlContent = "";
let promoInfoCards = [];
let hasMarkdownCtaTokens = false;
let shouldOpenCashierAfterAuth = false;
let isComponentVisible = false;
let interval;
let allPromotions = [];
let isValidPromo = true;
let isLoading = true;
let isFetching = false;
let isRacePage = false;
const BRAND_NAME = cfgBrandTitle();
/** Promo CDN copy may use `%(brand)%` / `%(domain)%` as well as `%brand%` / `%domain%`. */
const PROMO_BRAND_PLACEHOLDER_RE = /%\(?(domain|brand)\)?%/g;
let active = false;
const parseMarkdownWithEmbeddedCtas = (markdownText) => {
	const segments = [];
	let hasTokens = false;
	let cursor = 0;
	let markdownBuffer = "";
	CTA_TOKEN_REGEX.lastIndex = 0;
	let match;
	const pushMarkdownSegment = (value) => {
		if (!value) return;
		segments.push({
			type: "markdown",
			html: md.render(value)
		});
	};
	while ((match = CTA_TOKEN_REGEX.exec(markdownText)) !== null) {
		hasTokens = true;
		markdownBuffer += markdownText.slice(cursor, match.index);
		const isLoggedOutToken = match[1] !== undefined && match[2] !== undefined;
		const isLoggedInToken = match[3] !== undefined && match[4] !== undefined;
		const rawText = (match[1] ?? match[3] ?? match[5] ?? "").trim();
		const rawDestination = (match[2] ?? match[4] ?? match[6] ?? "").trim();
		const visibility = isLoggedOutToken ? "loggedOut" : isLoggedInToken ? "loggedIn" : "both";
		if (!rawText || !rawDestination) {
			markdownBuffer += match[0];
			cursor = CTA_TOKEN_REGEX.lastIndex;
			continue;
		}
		pushMarkdownSegment(markdownBuffer);
		markdownBuffer = "";
		segments.push({
			type: "cta",
			cta: {
				text: rawText,
				destination: rawDestination,
				visibility
			}
		});
		cursor = CTA_TOKEN_REGEX.lastIndex;
	}
	markdownBuffer += markdownText.slice(cursor);
	pushMarkdownSegment(markdownBuffer);
	return {
		segments,
		hasTokens
	};
};
const splitMarkdownIntoInfoCardSections = (markdownText) => {
	const normalized = markdownText.replace(/\r\n/g, "\n");
	return normalized.split(PROMO_NEXT_CARD_MARKER).map((s) => s.trim()).filter((s) => s.length > 0);
};
const isCtaVisibleForCurrentUser = (visibility) => {
	const isLoggedIn = $loggedIn === true;
	if (visibility === "loggedOut") return !isLoggedIn;
	if (visibility === "loggedIn") return isLoggedIn;
	return true;
};
const openCashierWithAuthFlow = () => {
	if ($loggedIn) {
		pushModal(ModalDialog.Cashier);
		return;
	}
	shouldOpenCashierAfterAuth = true;
	pushModal(ModalDialog.Auth, { tab: "login" });
};
const handleEmbeddedCtaClick = async (destination) => {
	if (destination === "open_cashier") {
		openCashierWithAuthFlow();
		return;
	}
	await goto(toSiteUrl(destination, $loggedIn === true));
};
const checkActive = () => {
	const now = new Date();
	active = !!(!promo?.startDate && !promo?.endDate || promo?.startDate && !promo?.endDate && now >= promo?.startDate || !promo?.startDate && promo?.endDate && now <= promo?.endDate || promo?.startDate && promo?.endDate && now >= promo?.startDate && now <= promo?.endDate || $stateFeatureSet === FeatureSet.Preview);
};
async function fetchContent(lang) {
	try {
		if (promo?.infoAndTermsSeparated) {
			const infoPath = cfgToThemeContentPath(`/promotions/${promo.id}/${promo.id}-info.${lang}.md`);
			const termsPath = cfgToThemeContentPath(`/promotions/${promo.id}/${promo.id}-terms.${lang}.md`);
			const [infoRes, termsRes] = await Promise.all([fetch(infoPath), fetch(termsPath)]);
			let infoText = infoRes.ok ? await infoRes.text() : "";
			let termsText = termsRes.ok ? await termsRes.text() : "";
			infoText = infoText.replace(PROMO_BRAND_PLACEHOLDER_RE, BRAND_NAME);
			termsText = termsText.replace(PROMO_BRAND_PLACEHOLDER_RE, BRAND_NAME);
			const parsedInfo = parseMarkdownWithEmbeddedCtas(infoText);
			const parsedTerms = parseMarkdownWithEmbeddedCtas(termsText);
			promoInfoCards = [[{
				className: "promo-info-block",
				segments: parsedInfo.segments
			}, {
				className: "promo-terms-block",
				segments: parsedTerms.segments
			}]];
			hasMarkdownCtaTokens = parsedInfo.hasTokens || parsedTerms.hasTokens;
			htmlContent = "";
			return;
		}
		// regular flow
		const response = await fetch(content);
		if (!response.ok) {
			throw new Error(`Error fetching content: ${response.statusText}`);
		}
		let text = await response.text();
		text = text.replace(PROMO_BRAND_PLACEHOLDER_RE, BRAND_NAME);
		if (text.includes("Page not found")) {
			htmlContent = `<p>${$t(Verbiage.no_info_available)}</p>`;
			promoInfoCards = [];
			hasMarkdownCtaTokens = false;
			return;
		}
		const sections = splitMarkdownIntoInfoCardSections(text);
		if (sections.length === 0) {
			const parsedContent = parseMarkdownWithEmbeddedCtas(text);
			promoInfoCards = [[{
				className: "",
				segments: parsedContent.segments
			}]];
			hasMarkdownCtaTokens = parsedContent.hasTokens;
			htmlContent = "";
			return;
		}
		if (sections.length === 1) {
			const parsedContent = parseMarkdownWithEmbeddedCtas(sections[0]);
			promoInfoCards = [[{
				className: "",
				segments: parsedContent.segments
			}]];
			hasMarkdownCtaTokens = parsedContent.hasTokens;
			htmlContent = "";
			return;
		}
		const firstParsed = parseMarkdownWithEmbeddedCtas(sections[0]);
		const trailingCards = sections.slice(1).map((body) => [{
			className: "",
			segments: [{
				type: "markdown",
				html: md.render(body)
			}]
		}]);
		promoInfoCards = [[{
			className: "",
			segments: firstParsed.segments
		}], ...trailingCards];
		hasMarkdownCtaTokens = firstParsed.hasTokens;
		htmlContent = "";
	} catch (error) {
		console.error("Failed to fetch content:", error);
		htmlContent = "";
		promoInfoCards = [];
		hasMarkdownCtaTokens = false;
	}
}
$: if (shouldOpenCashierAfterAuth && $loggedIn) {
	shouldOpenCashierAfterAuth = false;
	pushModal(ModalDialog.Cashier);
}
$: if (shouldOpenCashierAfterAuth && !$loggedIn && !$modalStack.some((modalEntry) => modalEntry.modal === ModalDialog.Auth)) {
	shouldOpenCashierAfterAuth = false;
}
$: {
	const observer = new IntersectionObserver((entries) => {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				isComponentVisible = true;
				startAutoRefresh();
			} else {
				isComponentVisible = false;
				clearInterval(interval);
			}
		});
	}, { threshold: .5 });
	// Start the interval to refresh FlushRaces every 3 minutes
	function startAutoRefresh() {
		if (interval) clearInterval(interval);
		interval = setInterval(() => {}, 18e4);
	}
	onDestroy(() => {
		clearInterval(interval);
	});
}
const initSub = derived([
	page,
	stateFeatureSet,
	stateTheme,
	locale,
	stateRaces
], async ([page_, stateFeatureSet_, stateTheme_, locale_, stateRaces_]) => {
	if (isFetching) {
		return;
	}
	isFetching = true;
	try {
		const pathSegments = page_.url.pathname.split("/").filter(Boolean);
		const lastSegment = pathSegments[pathSegments.length - 1];
		isRacePage = pathSegments.includes("race");
		if (isRacePage) {
			refreshRaces();
			const racePromos = mapRacesToPromotions(stateRaces_ ?? [], true);
			allPromotions = [...getPromotions(stateFeatureSet_, stateTheme_, locale_, $loggedIn), ...racePromos];
		} else {
			allPromotions = getPromotions(stateFeatureSet_, stateTheme_, locale_, $loggedIn);
		}
		const paramId = lastSegment;
		promo = allPromotions.find((p) => p.id === paramId) || promo;
		isValidPromo = !!promo;
		const lang = locale_;
		if (promo && !isRacePage) {
			content = cfgToThemeContentPath(`/promotions/${promo?.id}/${promo?.id}.${lang}.md`);
			await fetchContent(lang);
		}
	} catch (error) {
		console.error("Error fetching data:", error);
	} finally {
		isLoading = false;
		isFetching = false;
	}
}).subscribe(() => {});
onMount(() => {
	checkActive();
	return () => {
		initSub();
	};
});
$: useAlternative = $stateUi.promotions.promotionsStyle === "alternative";
$: useMomentum = $stateUi.promotions.promotionsStyle === "momentum";
</script>

<Container>
	{#if isLoading}
		<div class="loading" />
	{:else if isValidPromo}
		<div class="promotion" class:promotion-momentum={useMomentum}>
			{#if isFlushRace}
				<Race />
			{:else}
				<div class="promo-card">
					{#if useAlternative}
						<PromoItemCustom {promo} isNew={false} highlight={true} isDetail />
					{:else if useMomentum}
						<PromoItemMomentum {promo} />
					{:else}
						<PromoItem {promo} isNew={false} highlight={true} isDetail />
					{/if}
				</div>
				<div class="promo-information">
					{#if promoInfoCards.length > 0}
						{#each promoInfoCards as cardBlocks, cardIndex}
							<div class="promo-information-card">
								<InfoCard highlight={true}>
									<div class="promo-information-content">
										<div
											class="promo-markdown"
											class:promo-markdown--secondary={cardIndex > 0 && !useMomentum}
											style={`--promo-markdown-image-max-width: ${$isMobile ? '100%' : '50%'};`}
										>
											{#each cardBlocks as contentBlock}
												<div class={contentBlock.className}>
													{#each contentBlock.segments as segment}
														{#if segment.type === 'markdown'}
															{@html segment.html}
														{:else if isCtaVisibleForCurrentUser(segment.cta.visibility)}
															<div class="promo-embedded-cta">
																<Button
																	text={segment.cta.text}
																	variant="primary"
																	minWidth={true}
																	onClick={() => handleEmbeddedCtaClick(segment.cta.destination)}
																/>
															</div>
														{/if}
													{/each}
												</div>
											{/each}
										</div>
										{#if cardIndex === 0}
											<slot name="promo-information"></slot>
											{#if $stateUi.promotions?.showActionButton && !hasMarkdownCtaTokens && !useMomentum}
												<ActionButton isDetail={true} {active} {promo} />
											{/if}
										{/if}
									</div>
								</InfoCard>
							</div>
						{/each}
					{:else}
						<div class="promo-information-card">
							<InfoCard highlight={true}>
								<div class="promo-information-content">
									<div
										class="promo-markdown"
										style={`--promo-markdown-image-max-width: ${$isMobile ? '100%' : '50%'};`}
									>
										{@html htmlContent}
									</div>
									<slot name="promo-information"></slot>
									{#if $stateUi.promotions?.showActionButton && !hasMarkdownCtaTokens && !useMomentum}
										<ActionButton isDetail={true} {active} {promo} />
									{/if}
								</div>
							</InfoCard>
						</div>
					{/if}
				</div>
			{/if}
		</div>
	{:else}
		<NotFound />
	{/if}
</Container>

<style>
	.promotion {
		display: flex;
		width: 100%;
		flex-direction: column;
		align-items: center;
		gap: 3rem;
}

	.promotion-momentum {
		gap: 1rem;
}

	.promo-information {
		display: flex;
		width: 100%;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 2rem;
}

	.promo-information-card {
		width: 100%;
}

	.promo-information-content {
		display: flex;
		width: 100%;
		max-width: 100%;
		flex-direction: column;
		align-items: center;
		gap: 1rem;
}

	@media (min-width: 1024px) {
		.promo-information-content {
				padding: 1rem;
		}
	}

	.promo-card {
		width: 100%;
}

	:global(.promo-markdown) {
		width: 100%;
		max-width: 100%;
		overflow: visible;
		overflow-wrap: break-word;
		text-align: start;
		font-size: 16px;
		line-height: 1.5rem;
}

	@media (min-width: 1024px) {
		:global(.promo-markdown) {
				text-align: start;
		}
	}

	:global(.promo-markdown h2),
	:global(.promo-markdown h3),
	:global(.promo-markdown h4),
	:global(.promo-markdown h5),
	:global(.promo-markdown h6) {
		margin-bottom: 0.5rem;
		text-align: start;
		font-size: 16px;
		font-weight: 700;
		line-height: 1.5rem;
		font-family: var(--font-main);
}

	/* Solo el título principal (h1): 24px; el resto del markdown queda en 16px. */
	:global(.promo-markdown h1) {
		margin-bottom: 0.5rem;
		text-align: center;
		font-size: 24px;
		font-weight: 700;
		font-style: normal;
		line-height: 2rem;
		font-family: var(--font-main);
}

	:global(.promo-markdown p) {
		margin-bottom: 1.5rem;
		font-size: 16px;
		font-weight: 400;
		line-height: 1.5rem;
}

	/* Secondary InfoCards: dim body copy at 70%; headings stay full opacity.
	   Opacity on top-level `ul`/`ol`/`p`/`blockquote` only — not on each `li` (nested lists would compound). */
	:global(.promo-markdown--secondary > div > p),
	:global(.promo-markdown--secondary > div > ul),
	:global(.promo-markdown--secondary > div > ol),
	:global(.promo-markdown--secondary > div > blockquote),
	:global(.promo-markdown--secondary td),
	:global(.promo-markdown--secondary tbody th) {
		opacity: 0.7;
}

	:global(.promo-markdown ul),
	:global(.promo-markdown ol) {
		margin-bottom: 1rem;
		gap: 0.625rem;
		padding-left: 1.5rem;
		font-size: 16px;
		line-height: 1.5rem;
		font-family: var(--font-main);
}

	:global(.promo-markdown li) {
		margin-bottom: 0.25rem;
		font-size: 16px;
		line-height: 1.5rem;
		font-family: var(--font-main);
}

	:global(.promo-markdown .promo-embedded-cta) {
		margin-bottom: 1.5rem;
		display: flex;
		width: 100%;
		justify-content: center;
}

	:global(.promo-markdown ul) {
		list-style-type: disc;
}

	:global(.promo-markdown ol) {
		list-style-type: decimal;
}

	:global(.promo-markdown hr) {
		display: none;
}

	:global(.promo-markdown a) {
		font-weight: 500;
		color: var(--color-primary-500);
		text-decoration-line: underline;
		transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transition-duration: 150ms;
}

	:global(.promo-markdown a:hover) {
		color: var(--color-primary-700);
}

	:global(.promo-markdown br) {
		display: block;
		height: 1rem;
		--tw-content: '';
		content: var(--tw-content);
}

	:global(.promo-markdown img) {
		max-width: var(--promo-markdown-image-max-width, 100%);
		height: auto;
	}

	:global(.promo-information .promo-markdown) {
		width: 100%;
		max-width: 100%;
		overflow-x: auto;
}

	:global(.promo-information .promo-markdown .table-scroll-wrapper) {
		overflow-x: auto;
		-webkit-overflow-scrolling: touch;
		width: 100%;
		margin-bottom: 1rem;
	}

	:global(.promo-information .promo-markdown table) {
		overflow: hidden;
		border-radius: 0.75rem;
		border-width: 1px;
		border-color: rgb(255 255 255 / 0.1);
		font-size: 16px;
		border-collapse: separate !important;
		border-spacing: 0;
		width: -moz-max-content;
		width: max-content;
		min-width: 100%;
}

	:global(.promo-information .promo-markdown thead th) {
		border-bottom-width: 1px;
		border-color: rgb(255 255 255 / 0.1);
		background-color: var(--color-background-100);
		padding-left: 1rem;
		padding-right: 1rem;
		padding-top: 0.5rem;
		padding-bottom: 0.5rem;
		text-align: left;
		font-size: 16px;
		font-weight: 600;
}

	:global(.promo-information .promo-markdown tbody td) {
		border-bottom-width: 1px;
		border-color: rgb(255 255 255 / 0.1);
		padding-left: 1rem;
		padding-right: 1rem;
		padding-top: 0.5rem;
		padding-bottom: 0.5rem;
		vertical-align: middle;
		font-size: 16px;
}

	:global(.promo-information .promo-markdown tbody tr:nth-child(even)) {
		background-color: var(--color-background-100);
}

	:global(.promo-information .promo-markdown thead tr:first-child th:first-child) {
		border-top-left-radius: 0.75rem;
	}
	:global(.promo-information .promo-markdown thead tr:first-child th:last-child) {
		border-top-right-radius: 0.75rem;
	}
	:global(.promo-information .promo-markdown tbody tr:first-child td:first-child) {
		border-top-left-radius: 0.75rem;
	}
	:global(.promo-information .promo-markdown tbody tr:first-child td:last-child) {
		border-top-right-radius: 0.75rem;
	}
	:global(.promo-information .promo-markdown tbody tr:last-child td:first-child) {
		border-bottom-left-radius: 0.75rem;
	}
	:global(.promo-information .promo-markdown tbody tr:last-child td:last-child) {
		border-bottom-right-radius: 0.75rem;
	}

	:global(.promo-information .promo-markdown table a) {
		font-weight: 500;
		color: var(--color-primary-500);
		text-decoration-line: underline;
		transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		transition-duration: 150ms;
}
	:global(.promo-information .promo-markdown table a:hover) {
		color: var(--color-primary-700);
}

	:global(.promo-information .promo-markdown h1 + .table-scroll-wrapper),
	:global(.promo-information .promo-markdown h2 + .table-scroll-wrapper),
	:global(.promo-information .promo-markdown h3 + .table-scroll-wrapper),
	:global(.promo-information .promo-markdown h4 + .table-scroll-wrapper),
	:global(.promo-information .promo-markdown h5 + .table-scroll-wrapper),
	:global(.promo-information .promo-markdown h6 + .table-scroll-wrapper) {
		margin-top: 1rem;
}

	:global(.promo-information .promo-markdown table tr > *:last-child) {
		text-align: right;
}

	:global(.promo-information .promo-markdown table caption) {
		padding-left: 1rem;
		padding-right: 1rem;
		padding-top: 0.75rem;
		padding-bottom: 0.25rem;
		text-align: left;
		font-size: 16px;
		--tw-text-opacity: 1;
		color: rgb(156 163 175 / var(--tw-text-opacity, 1));
}
</style>
